import { useState, useMemo } from 'react';
import {
    useGetDiscoveryMoveGroupsQuery,
    useGetApplicationsQuery,
    useLazyGetApplicationByIdQuery,
    useCreateMoveGroupMutation,
    useUpdateMoveGroupMutation,
    useDeleteMoveGroupMutation,
    useCreateApplicationMutation,
    useUpdateApplicationMutation,
    useDeleteApplicationMutation,
    useAssignVMsToMoveGroupOrApplicationMutation,
    useUnassignMoveGroupAssignmentsMutation,
    useGetMoveGroupByIdQuery,
    useGetApplicationByIdQuery,
    useUpdateInventoryStatusAndCriteriaMutation,
    useGetInventoryVMInfoQuery,
} from '../../../api/discovery';
import { useGetUsersQuery } from '../../../api/users';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { formatDateToUTC } from '../../../lib/helpers';

// Find an item in a list by id, tolerating id values passed as either a string or a number
const findById = (list, value) => {
    const numericValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return list.find((item) => item.id === numericValue || item.id === value);
};

// Helper function to separate new application names from existing IDs
const separateNewAndExistingApps = (values = [], applicationsList = []) => {
    const newApplicationNames = [];
    const existingApplicationIds = [];

    values.forEach((value) => {
        const existingApp = findById(applicationsList, value);
        if (existingApp) {
            existingApplicationIds.push(existingApp.id);
        } else {
            newApplicationNames.push(value);
        }
    });

    return { newApplicationNames, existingApplicationIds };
};

/**
 * Manages all data and operations for Move Groups and Applications
 */
export const useMoveGroupsAndAppsData = ({ refreshNotifications } = {}) => {
    const orgId = useMemo(() => sessionStorage.getItem('userOrg'), []);

    // Modal state
    const [moveGroupModalOpen, setMoveGroupModalOpen] = useState(false);
    const [applicationModalOpen, setApplicationModalOpen] = useState(false);
    const [manageModalOpen, setManageModalOpen] = useState(false);
    const [editMoveGroupModalOpen, setEditMoveGroupModalOpen] = useState(false);
    const [editApplicationModalOpen, setEditApplicationModalOpen] = useState(false);

    // Selection state
    const [editingMoveGroup, setEditingMoveGroup] = useState(null);
    const [editingApplication, setEditingApplication] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedApp, setSelectedApp] = useState(null);
    const [selectedInventory, setSelectedInventory] = useState(true);
    const [selectedUnassigned, setSelectedUnassigned] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const { notifySuccess, notifyError } = useNotificationContext();

    // Fetch move groups and applications from API
    const { data: moveGroupsData, isLoading: moveGroupsLoading } = useGetDiscoveryMoveGroupsQuery(orgId, {
        skip: !orgId,
        refetchOnMountOrArgChange: true,
    });

    const { data: applicationsData, isLoading: applicationsLoading } = useGetApplicationsQuery(orgId, {
        skip: !orgId,
        refetchOnMountOrArgChange: true,
    });

    const { data: usersData, isLoading: usersLoading } = useGetUsersQuery(orgId, {
        skip: !orgId,
        refetchOnMountOrArgChange: true,
    });

    // Fetch 'all' inventory count, only totalElements is needed for this page
    const { data: unsortedInventoryData, isLoading: unsortedInventoryLoading } = useGetInventoryVMInfoQuery(
        {
            orgId,
            size: 1,
            page: 0,
        },
        {
            skip: !orgId,
            refetchOnMountOrArgChange: true,
        },
    );

    const unsortedMachinesCount = unsortedInventoryData?.page?.totalElements || 0;

    // Fetch unassigned inventory
    const { data: unassignedInventoryData, isLoading: unassignedInventoryLoading } = useGetInventoryVMInfoQuery(
        {
            orgId,
            size: 1,
            page: 0,
            unassigned: true,
        },
        {
            skip: !orgId,
            refetchOnMountOrArgChange: true,
        },
    );

    const unassignedMachinesCount = unassignedInventoryData?.page?.totalElements || 0;

    // Mutations
    const [createMoveGroup] = useCreateMoveGroupMutation();
    const [updateMoveGroup] = useUpdateMoveGroupMutation();
    const [deleteMoveGroup] = useDeleteMoveGroupMutation();
    const [createApplication] = useCreateApplicationMutation();
    const [updateApplication] = useUpdateApplicationMutation();
    const [deleteApplication] = useDeleteApplicationMutation();
    const [assignVMsToMoveGroupOrApplication] = useAssignVMsToMoveGroupOrApplicationMutation();
    const [unassignMoveGroupAssignments] = useUnassignMoveGroupAssignmentsMutation();
    const [getApplicationById] = useLazyGetApplicationByIdQuery();
    const [updateInventoryStatusAndCriteria] = useUpdateInventoryStatusAndCriteriaMutation();

    // Add pagination state
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const handlePaginationChange = (page, pageSize) => {
        setPagination((prev) => ({ ...prev, current: page, pageSize }));
    };

    const {
        data: moveGroupData,
        isLoading: moveGroupLoading,
        isFetching: moveGroupFetching,
    } = useGetMoveGroupByIdQuery(
        {
            moveGroupId: selectedGroup,
            size: pagination.pageSize,
            page: pagination.current - 1,
        },
        { skip: !selectedGroup },
    );

    const {
        data: applicationData,
        isLoading: applicationLoading,
        isFetching: applicationFetching,
    } = useGetApplicationByIdQuery(
        {
            applicationId: selectedApp,
            size: pagination.pageSize,
            page: pagination.current - 1,
        },
        { skip: !selectedApp },
    );

    // Transform API data to the format expected by components
    const moveGroups = useMemo(() => {
        if (!moveGroupsData) return [];
        const content = moveGroupsData.content || moveGroupsData || [];
        return content.map((group) => ({
            id: group.id,
            name: group.name,
            machines: group.vms_count || 0,
            apps: group.applications?.length || 0,
            applications: group.applications || [],
            application_ids: group.application_ids || group.applications?.map((app) => app.id) || [],
            externalId: group.external_id,
            assigned_resource: group.assigned_resource,
            assigned_resource_email: group.assigned_resource_email,
            target_migration_date: group.target_migration_date,
            vms: group.vms || [],
        }));
    }, [moveGroupsData]);

    const applications = useMemo(() => {
        if (!applicationsData) return [];
        const content = applicationsData.content || applicationsData || [];
        return content.map((app) => ({
            id: app.id,
            name: app.name,
            machines: app.vms_count || 0,
            group: app.move_group_name || null,
            moveGroupId: app.move_group_id || null,
            move_group_ids: app.move_group_ids || [],
            externalId: app.external_id,
            vms: app.vms || [],
        }));
    }, [applicationsData]);

    // Options for dropdowns
    const moveGroupOptions = useMemo(
        () =>
            moveGroups.map((group) => ({
                value: group.id,
                label: group.name,
            })),
        [moveGroups],
    );

    const applicationOptions = useMemo(
        () =>
            applications.map((app) => ({
                value: app.id,
                label: app.name,
                move_group_ids: app.move_group_ids || [],
            })),
        [applications],
    );

    const userOptions = useMemo(() => {
        if (!usersData) return [];
        const content = usersData.content || usersData || [];
        return content.map((user) => {
            return {
                value: user.id,
                label: user.name || user.email || user.username,
            };
        });
    }, [usersData]);

    // Determine if there are any groups or apps
    const hasGroupsOrApps = moveGroups.length > 0 || applications.length > 0;

    // Resolve a dropdown value to an entity id: the existing entity's id, or a newly created one
    const resolveEntity = async (value, list, createFn) => {
        if (!value) return null;
        const existing = findById(list, value);
        if (existing) return existing.id;
        const created = await createFn(value);
        return created.id;
    };

    // Helper function to resolve a move group value (existing ID or new name to create)
    const resolveMoveGroup = (value) =>
        resolveEntity(value, moveGroups, (name) => 
            createMoveGroup({ organizationId: orgId, name }).unwrap());

    // Helper function to resolve an application value (existing ID or new name to create)
    const resolveApplication = (value, moveGroupId = null) =>
        resolveEntity(value, applications, (name) =>
            createApplication({
                organizationId: orgId,
                name,
                moveGroupIds: moveGroupId ? [moveGroupId] : [],
            }).unwrap(),
        );

    // Helper function to batch-update a set of applications' move-group assignment
    const updateApplicationsMoveGroups = async (appIds, moveGroupIds) => {
        const updatePromises = appIds.map((appId) => {
            const currentApp = applications.find((app) => app.id === appId);
            if (!currentApp) return null;
            return updateApplication({
                applicationId: appId,
                name: currentApp.name,
                moveGroupIds,
            }).unwrap();
        });
        return Promise.all(updatePromises.filter(Boolean));
    };

    // Create a move group, create any new applications, and update existing apps.
    const handleCreateMoveGroup = async (values, onCreated) => {
        try {
            const newGroup = await createMoveGroup({
                organizationId: orgId,
                name: values.name,
                externalId: values.externalId,
                user: values.user,
                date: formatDateToUTC(values.targetMigrationDate),
            }).unwrap();

            if (values.applications && values.applications.length > 0) {
                const { newApplicationNames, existingApplicationIds } = separateNewAndExistingApps(
                    values.applications,
                    applications,
                );

                if (newApplicationNames.length > 0) {
                    const createAppPromises = newApplicationNames.map(async (appName) => {
                        return createApplication({
                            organizationId: orgId,
                            name: appName,
                            moveGroupIds: [newGroup.id],
                        }).unwrap();
                    });
                    await Promise.all(createAppPromises);
                }

                if (existingApplicationIds.length > 0) {
                    await updateApplicationsMoveGroups(existingApplicationIds, [newGroup.id]);
                }
            }

            notifySuccess({ message: 'Move group created successfully.' });
            setMoveGroupModalOpen(false);
            onCreated?.(newGroup.id);
        } catch (error) {
            notifyError({ message: 'Failed to create move group.' });
        }
    };

    // Update a move group's properties; create new applications and update assignments.
    const handleUpdateMoveGroup = async (values) => {
        try {
            if (values.removeAssignment) {
                await unassignMoveGroupAssignments({ moveGroupId: values.id }).unwrap();
                notifySuccess({ message: 'Move group updated successfully.' });
                refreshNotifications?.();
            } else {
                await updateMoveGroup({
                    moveGroupId: values.id,
                    name: values.name,
                    user: values.user,
                    date: formatDateToUTC(values.targetMigrationDate),
                }).unwrap();

                if (values.removedApplications && values.removedApplications.length > 0) {
                    await updateApplicationsMoveGroups(values.removedApplications, []);
                }

                if (values.applications && values.applications.length > 0) {
                    const { newApplicationNames, existingApplicationIds } = separateNewAndExistingApps(
                        values.applications,
                        applications,
                    );

                    const newlyCreatedAppIds = [];
                    if (newApplicationNames.length > 0) {
                        const createAppPromises = newApplicationNames.map(async (appName) => {
                            const newApp = await createApplication({
                                organizationId: orgId,
                                name: appName,
                                moveGroupIds: [values.id],
                            }).unwrap();
                            return newApp.id;
                        });
                        const createdIds = await Promise.all(createAppPromises);
                        newlyCreatedAppIds.push(...createdIds);
                    }

                    if (existingApplicationIds.length > 0) {
                        await updateApplicationsMoveGroups(existingApplicationIds, [values.id]);
                    }
                }

                notifySuccess({ message: 'Move group updated successfully.' });
            }

            setEditMoveGroupModalOpen(false);
            setEditingMoveGroup(null);
        } catch (error) {
            notifyError({ message: 'Failed to update move group.' });
        }
    };

    // Create an application; create a new move group first if a new name is provided.
    const handleCreateApplication = async (values, onCreated) => {
        try {
            let moveGroupIdsToUse = [];

            if (values.moveGroupId) {
                const resolvedGroupId = await resolveMoveGroup(values.moveGroupId);
                if (resolvedGroupId) {
                    moveGroupIdsToUse = [resolvedGroupId];
                }
            }

            const newApp = await createApplication({
                organizationId: orgId,
                name: values.name,
                moveGroupIds: moveGroupIdsToUse,
                externalId: values.externalId,
            }).unwrap();

            notifySuccess({ message: 'Application created successfully.' });
            setApplicationModalOpen(false);
            onCreated?.(newApp.id);
        } catch (error) {
            notifyError({ message: 'Failed to create application.' });
        }
    };

    // Update an application and its move-group assignment, create a new move group if needed.
    const handleUpdateApplication = async (values) => {
        try {
            if (values.removeAssignment) {
                const { data: applicationDetails } = await getApplicationById({ applicationId: values.id });
                if (applicationDetails && applicationDetails.vms && applicationDetails.vms.length > 0) {
                    const vmIds = applicationDetails.vms.map((vm) => vm.id);
                    await assignVMsToMoveGroupOrApplication({
                        vmIds,
                        moveGroupId: null,
                        applicationId: null,
                    }).unwrap();
                }
                notifySuccess({ message: 'All VMs unassigned from application successfully.' });
            } else {
                let moveGroupIdsToUse = [];

                if (!values.unassignFromMoveGroup && values.moveGroupId) {
                    const resolvedGroupId = await resolveMoveGroup(values.moveGroupId);
                    if (resolvedGroupId) {
                        moveGroupIdsToUse = [resolvedGroupId];
                    }
                }

                const payload = {
                    applicationId: values.id,
                    name: values.name,
                    moveGroupIds: values.unassignFromMoveGroup ? [] : moveGroupIdsToUse,
                };
                await updateApplication(payload).unwrap();

                if (values.unassignFromMoveGroup) {
                    notifySuccess({ message: 'Application unassigned from move group successfully.' });
                } else {
                    notifySuccess({ message: 'Application updated successfully.' });
                }
            }

            setEditApplicationModalOpen(false);
            setEditingApplication(null);
        } catch (error) {
            notifyError({ message: 'Failed to update application.' });
        }
    };

    // Handler for unassigning an application from its move group (from sidebar context menu)
    const handleUnassignApplication = async (application) => {
        try {
            const currentApp = applications.find((app) => app.id === application.id);

            if (!currentApp) {
                notifyError({ message: 'Application not found.' });
                return;
            }

            // Update application to remove move group assignment
            await updateApplication({
                applicationId: currentApp.id,
                name: currentApp.name,
                moveGroupIds: [],
            }).unwrap();

            notifySuccess({ message: 'Application unassigned from move group successfully.' });
        } catch (error) {
            notifyError({ message: 'Failed to unassign application from move group.' });
        }
    };

    // Assign or unassign VMs to move groups/applications, create groups/apps when necessary.
    const handleManageApply = async (values) => {
        try {
            const { moveGroup, application, unassign } = values;

            if (unassign) {
                await assignVMsToMoveGroupOrApplication({
                    vmIds: selectedRowKeys,
                    moveGroupId: null,
                    applicationId: null,
                }).unwrap();
                notifySuccess({ message: 'VMs unassigned successfully.' });
            } else {
                let moveGroupIdToUse = await resolveMoveGroup(moveGroup);

                let applicationIdToUse = null;
                if (application) {
                    const existingApp = findById(applications, application);

                    if (existingApp) {
                        applicationIdToUse = existingApp.id;
                        if (!moveGroup && existingApp.move_group_ids && existingApp.move_group_ids.length > 0) {
                            moveGroupIdToUse = existingApp.move_group_ids[0];
                        }
                    } else {
                        applicationIdToUse = await resolveApplication(application, moveGroupIdToUse);
                    }
                }

                await assignVMsToMoveGroupOrApplication({
                    vmIds: selectedRowKeys,
                    moveGroupId: moveGroupIdToUse,
                    applicationId: applicationIdToUse,
                }).unwrap();
                notifySuccess({ message: 'VMs assigned successfully.' });
            }

            setManageModalOpen(false);
            setSelectedRowKeys([]);
            refreshNotifications?.();
        } catch (error) {
            notifyError({ message: 'Failed to update VM assignments.' });
        }
    };

    // Handler for selection change from table
    const handleSelectionChange = (keys) => {
        setSelectedRowKeys(keys);
    };

    // Handler for editing a move group
    const handleEditMoveGroup = (group) => {
        setEditingMoveGroup(group);
        setEditMoveGroupModalOpen(true);
    };

    // Handler for editing an application
    const handleEditApplication = (app) => {
        setEditingApplication(app);
        setEditApplicationModalOpen(true);
    };

    // Factory for delete handlers: clears the selection before the delete request goes out (so any
    // detail query for the entity unsubscribes before the response invalidates its cache tag and
    // triggers a refetch of the now-deleted entity), and restores the selection if the delete fails.
    const makeDeleteHandler =
        ({ deleteMutation, isSelected, onSelect, entityLabel }) =>
        async (id) => {
            const wasSelected = isSelected(id);
            if (wasSelected) {
                onSelect(null);
                setSelectedInventory(true);
            }
            try {
                await deleteMutation(id).unwrap();
                notifySuccess({ message: `${entityLabel} deleted successfully.` });
            } catch (error) {
                notifyError({ message: `Failed to delete ${entityLabel.toLowerCase()}.` });
                if (wasSelected) {
                    onSelect(id);
                    setSelectedInventory(false);
                }
            }
        };

    // Handler for deleting a move group
    const handleDeleteMoveGroup = makeDeleteHandler({
        deleteMutation: deleteMoveGroup,
        isSelected: (id) => selectedGroup === id,
        onSelect: setSelectedGroup,
        entityLabel: 'Move group',
    });

    // Handler for deleting an application
    const handleDeleteApplication = makeDeleteHandler({
        deleteMutation: deleteApplication,
        isSelected: (id) => selectedApp === id,
        onSelect: setSelectedApp,
        entityLabel: 'Application',
    });

    // Handler for changing status
    const handleChangeStatus = async (selectedIds, status) => {
        try {
            await updateInventoryStatusAndCriteria({
                orgId,
                inventoryIds: selectedIds,
                status,
            }).unwrap();
            notifySuccess({ message: 'Status updated successfully.' });
        } catch (error) {
            notifyError({ message: 'Failed to update status.' });
        }
    };

    const isLoading = moveGroupsLoading || applicationsLoading;

    // Handler for drag assignment of VMs to move group or application
    const handleDragAssignVMs = async (vmIds, targetMoveGroupId, targetApplicationId) => {
        try {
            await assignVMsToMoveGroupOrApplication({
                vmIds,
                moveGroupId: targetMoveGroupId,
                applicationId: targetApplicationId,
            }).unwrap();
            notifySuccess({ message: 'VMs assigned successfully.' });
            refreshNotifications?.();
        } catch (error) {
            notifyError({ message: 'Failed to assign VMs.' });
        }
    };

    // Handler for drag reassignment of application to move group
    const handleDragReassignApplication = async (applicationId, targetMoveGroupId) => {
        try {
            const app = applications.find((a) => a.id === applicationId);
            if (!app) {
                notifyError({ message: 'Application not found.' });
                return;
            }

            await updateApplication({
                applicationId: app.id,
                name: app.name,
                moveGroupIds: targetMoveGroupId ? [targetMoveGroupId] : [],
            }).unwrap();

            notifySuccess({ message: 'Application reassigned successfully.' });
        } catch (error) {
            notifyError({ message: 'Failed to reassign application.' });
        }
    };

    return {
        // State
        moveGroupModalOpen,
        setMoveGroupModalOpen,
        applicationModalOpen,
        setApplicationModalOpen,
        manageModalOpen,
        setManageModalOpen,
        editMoveGroupModalOpen,
        setEditMoveGroupModalOpen,
        editApplicationModalOpen,
        setEditApplicationModalOpen,
        editingMoveGroup,
        setEditingMoveGroup,
        editingApplication,
        setEditingApplication,
        selectedGroup,
        setSelectedGroup,
        selectedApp,
        setSelectedApp,
        selectedInventory,
        setSelectedInventory,
        selectedUnassigned,
        setSelectedUnassigned,
        selectedRowKeys,

        // Data
        moveGroups,
        applications,
        moveGroupOptions,
        applicationOptions,
        userOptions,
        hasGroupsOrApps,
        isLoading,
        unsortedMachinesCount,
        unsortedInventoryLoading,
        unassignedMachinesCount,
        unassignedInventoryLoading,

        moveGroupData,
        applicationData,
        moveGroupLoading,
        moveGroupFetching,
        applicationLoading,
        applicationFetching,
        pagination,
        handlePaginationChange,

        // Handlers
        handleCreateMoveGroup,
        handleUpdateMoveGroup,
        handleCreateApplication,
        handleUpdateApplication,
        handleManageApply,
        handleSelectionChange,
        handleEditMoveGroup,
        handleEditApplication,
        handleDeleteMoveGroup,
        handleDeleteApplication,
        handleUnassignApplication,
        handleChangeStatus,
        handleDragAssignVMs,
        handleDragReassignApplication,
    };
};
